import React, { useEffect, useState } from "react";
import { CalendarBlank } from "@phosphor-icons/react";
import { getCurrentGwStatus } from "../lib/api";

/**
 * Compact "GW {n}" badge rendered in the global header on every page.
 *
 * Uses a tiny module-level cache (60 s TTL) so navigating between pages
 * doesn't refetch — the FPL bootstrap is heavy upstream even though our
 * /api/season/current-gw endpoint is cheap.
 *
 * `max_gw` is also surfaced on the cache so callers (e.g. range pickers)
 * can clip user input at GW 38 when the season is wrapping up.
 */
const TTL_MS = 60_000;
let _cache = null;        // {data, ts}
let _inflight = null;     // de-dup concurrent requests

async function _load() {
    const now = Date.now();
    if (_cache && (now - _cache.ts) < TTL_MS) return _cache.data;
    if (_inflight) return _inflight;
    _inflight = getCurrentGwStatus()
        .then((d) => { _cache = { data: d, ts: Date.now() }; return d; })
        .catch((err) => { console.warn("GwBadge: fetch failed", err); return null; })
        .finally(() => { _inflight = null; });
    return _inflight;
}

export default function GwBadge() {
    const [data, setData] = useState(_cache?.data || null);

    useEffect(() => {
        let cancelled = false;
        _load().then((d) => { if (!cancelled && d) setData(d); });
        return () => { cancelled = true; };
    }, []);

    if (!data) {
        return (
            <div
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 border border-fa-cream/15 bg-black/40 font-mono text-[10px] uppercase tracking-widest text-white/40"
                data-testid="gw-badge-loading"
            >
                <CalendarBlank size={11} weight="bold" />
                GW —
            </div>
        );
    }

    const gw = data.active_gw ?? "—";
    const isFinal = data.season_complete;
    const cls = isFinal
        ? "border-orange-400/40 text-orange-300 bg-orange-500/10"
        : "border-fa-cream/40 text-fa-cream bg-fa-violet/10";

    return (
        <div
            className={`flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest ${cls}`}
            data-testid="gw-badge"
            title={isFinal ? "Mezi sezónami · čekáme na start 2026-27" : `Gameweek ${gw} (max GW 38)`}
        >
            <CalendarBlank size={11} weight="fill" />
            {isFinal ? "MEZI SEZÓNAMI" : `GW ${gw}`}
        </div>
    );
}
